import { 
  writable,
  derived } from 'svelte/store';
import { 
  FilterType
} from './constants.js';

// stores

export const residents = writable([]);

export const accounts = writable([]);

export const user = writable({});

export const alertFilters = writable([]);

export const search = writable('');

export const alerts = writable([]);

export const alertCounts = writable([]);

export const ui = writable({
  displayDeleteResidentModal: false,
  displayDeleteAccountModal: false,
  displayTestAlertModal: false
});

// derived stores
export const minResidentsSelected = derived(residents, 
  ($residents) => $residents.some(resident => resident.selected));

export const streets = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.street.toUpperCase())
    .filter(street => street.trim().length)
    .sort()
  )
]);

export const cities = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.city.toUpperCase())
    .filter(city => city.trim().length)
    .sort()
  )
]);

export const routes = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.route.toUpperCase())
    .filter(route => route.trim().length)
    .sort()
  )
]);

export const states = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.state.toUpperCase())
    .filter(state => state.trim().length)
    .sort()
  )
]);

export const propertyTypes = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.property_type.toUpperCase())
    .filter(type => type.trim().length)
    .sort()
  )
]);

export const divisions = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.division.toUpperCase())
    .filter(division => division.trim().length)
    .sort()
  )
]);

export const wells = derived(residents, ($residents) => [
  ...new Set($residents
    .map(resident => resident.well.toUpperCase())
    .filter(well => well.trim().length)
    .sort()
  )
]);

export const filteredResidents = derived([residents, alertFilters, search], ([$residents, $alertFilters, $search]) => {
  if (!$alertFilters.length && !$search.length) return $residents.sort((a, b) => a.ubmax_acc - b.ubmax_acc);
  const streets = $alertFilters.filter(filter => filter.type === FilterType.STREET).map(filter => filter.value);
  const cities = $alertFilters.filter(filter => filter.type === FilterType.CITY).map(filter => filter.value);
  const routes = $alertFilters.filter(filter => filter.type === FilterType.ROUTE).map(filter => filter.value);
  const propertyTypes = $alertFilters.filter(filter => filter.type === FilterType.PROPERTY_TYPE).map(filter => filter.value);
  const divisions = $alertFilters.filter(filter => filter.type === FilterType.DIVISION).map(filter => filter.value);
  const wells = $alertFilters.filter(filter => filter.type === FilterType.WELL).map(filter => filter.value);
  const search = $search.indexOf(',') > 0 ? 
    $search.split(',') :
    $search;
  return $residents
    .filter(resident => {
      return (!streets.length || streets.some(street => street === resident.street.toUpperCase())) && 
      (!cities.length || cities.some(city => city === resident.city.toUpperCase())) &&
      (!routes.length || routes.some(route => route === resident.route.toUpperCase())) &&
      (!propertyTypes.length || propertyTypes.some(propertyType => propertyType == resident.property_type.toUpperCase())) &&
      (!divisions.length || divisions.some(division => division === resident.division.toUpperCase())) &&
      (!wells.length || wells.some(well => well === resident.well.toUpperCase() ))
    })
    .filter(resident => {
      if (Array.isArray(search) && search.length) {
        return search.some(searchTerm => searchTerm.trim().length > 0 && resident.last_name.toUpperCase().trim().includes(searchTerm.trim().toUpperCase())) ||
          search.some(searchTerm => searchTerm.trim().length > 0  && resident.first_name.toUpperCase().trim().includes(searchTerm.trim().toUpperCase())) ||
          search.some(searchTerm => searchTerm.trim().length > 0 && resident.ubmax_acc.toString().trim().includes(searchTerm.toString().trim()))
      } else {
        return (!search.length || 
          resident.last_name.toUpperCase().trim().includes(search.toUpperCase().trim()) || 
          resident.first_name.toUpperCase().trim().includes(search.toUpperCase().trim()) || 
          resident.ubmax_acc.toString().trim().includes(search.toString().trim()))
      }
    })
    .sort((a, b) => a.ubmax_acc - b.ubmax_acc);
});

export const filteredAccounts = derived([accounts, search], ([$accounts, $search]) => {
  const search = $search.indexOf(',') > 0 ?
    $search.split(',') :
    $search;
  return $accounts  
    .filter(account => {
      if (Array.isArray(search) && search.length) {
        return search.some(searchTerm => { 
          searchTerm.trim().length > 0 && 
          (account.company.toUpperCase().trim().includes(searchTerm.trim().toUpperCase()) ||
            account.first_name.toUpperCase().trim().includes(searchTerm.trim().toUpperCase()) ||
            account.last_name.toUpperCase().trim().includes(searchTerm.trim().toUpperCase()) || 
            account.city.toUpperCase().trim().includes(searchTerm.trim().toUpperCase()) ||
            account.email.toUpperCase().trim().includes(searchTerm.trim().toUpperCase()));
        }); 
      } else {
        return !search.length ||
          account.company.toUpperCase().trim().includes(search.trim().toUpperCase()) ||
          account.first_name.toUpperCase().trim().includes(search.trim().toUpperCase()) ||
          account.last_name.toUpperCase().trim().includes(search.trim().toUpperCase()) || 
          account.city.toUpperCase().trim().includes(search.trim().toUpperCase()) ||
          account.email.toUpperCase().trim().includes(search.trim().toUpperCase());   
      }
    })
    .sort((a, b) => {
      const nameA = a.company.toUpperCase(); // ignore upper and lowercase
      const nameB = b.company.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
    
      // names must be equal
      return 0;
    });
});

export const sortedAlerts = derived([alerts], ([$alerts]) => {
  return $alerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp) );
});

export const filteredAlertCounts = derived([alertCounts, search], ([$alertCounts, $search]) => {
  return $alertCounts.filter(alertCount => {
    return $search.length === 0 || alertCount.account_name.toUpperCase().trim().includes($search.toUpperCase().trim());
  }); 
});

